import { parseZonedDateTime } from '@internationalized/date';

import {
  ExecutionRunRecord,
  ExecutionStatus,
  ExecutionRunSortOption,
} from '../types';

import { executionRunFragment } from './fragments';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export type ExecutionRunsListParams = {
  automationId?: string;
  status?: ExecutionStatus[];
  fromDate?: string;
  toDate?: string;
  isTest?: boolean;
  sorting?: ExecutionRunSortOption[];
  limit?: number;
  offset?: number;
};

export const executionRunsListQueryAtom = createQueryAtomFactory(
  'automations.executionRuns.list',
  (params: ExecutionRunsListParams) => ({
    executionRuns: {
      __args: {
        filter: {
          automationId: params.automationId,
          status: params.status,
          fromDate: params.fromDate,
          toDate: params.toDate,
          isTest: params.isTest,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 25,
          offset: params.offset ?? 0,
        },
      },
      page: {
        total: true,
      },
      entries: executionRunFragment,
    },
  }),
  {
    select: ({
      executionRuns,
    }): { total: number; runs: ExecutionRunRecord[] } => {
      type ExecutionRunGraphQLResult = {
        id: string;
        automationId: string;
        status: ExecutionStatus;
        triggeredAt: string;
        triggerPayload?: Record<string, unknown> | null;
        startedAt?: string | null;
        completedAt?: string | null;
        durationMs?: number | null;
        nodesExecuted?: number | null;
        actionsExecuted?: number | null;
        executionDurationMs?: number | null;
        errorMessage?: string | null;
        workflowId: string;
        workflowRunId: string;
        isTest?: boolean;
        organizationId?: string;
        timestamps: {
          createdAt: string;
          updatedAt: string;
        };
      };

      return {
        runs: executionRuns.entries.map((run: ExecutionRunGraphQLResult) => ({
          id: run.id,
          automationId: run.automationId,
          status: run.status as ExecutionStatus,
          triggeredAt: parseZonedDateTime(run.triggeredAt),
          triggerPayload: run.triggerPayload ?? null,
          startedAt: run.startedAt ? parseZonedDateTime(run.startedAt) : null,
          completedAt: run.completedAt
            ? parseZonedDateTime(run.completedAt)
            : null,
          durationMs: run.durationMs ?? null,
          nodesExecuted: run.nodesExecuted ?? null,
          actionsExecuted: run.actionsExecuted ?? null,
          executionDurationMs: run.executionDurationMs ?? null,
          errorMessage: run.errorMessage ?? null,
          workflowId: run.workflowId,
          workflowRunId: run.workflowRunId,
          isTest: run.isTest ?? false,
          organizationId: run.organizationId ?? '',
          timestamps: {
            createdAt: parseZonedDateTime(run.timestamps.createdAt),
            updatedAt: parseZonedDateTime(run.timestamps.updatedAt),
          },
        })),
        total: executionRuns.page.total ?? 0,
      };
    },
  },
);
